import clsx from "clsx";
import React from "react";
import { FC } from "react";
import { ComponentProps } from "react";
import { useMemo } from "react";
import { useCallback } from "react";
import { FieldRenderProps } from "react-final-form";
import { Theme } from "@material-ui/core/styles";
import MuiTextField from "@material-ui/core/TextField";
import InputLabel from "@material-ui/core/InputLabel";
import { InputLabelProps } from "@material-ui/core/InputLabel";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Typography from "@material-ui/core/Typography";
import InfoIcon from "../InfoIcon";

const useAutocompleteStyles = makeStyles(() => ({
  root: {
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input':
      {
        fontSize: 16,
        paddingTop: 3.5,
        paddingBottom: 3.5,
        opacity: 0.75,
      },
  },
  popper: {
    zIndex: 1400,
  },
}));

const Autocomplete: FC<ComponentProps<any>> = ({
  options = [],
  groupBy,
  freeSolo,
  limitTags,
  autoSelect,
  disabled,
  getOptionDisabled,
  size,
  getOptionLabel,
  disableClearable,
  disableCloseOnSelect,
  changeCallback,
  input: { onChange, clear, value, multiple, ...restInput } = {} as any,
  ...rest
}) => {
  const classes = useAutocompleteStyles();

  const handleGetOptionLabel = useCallback(
    (option) => {
      if (typeof getOptionLabel === "function") return getOptionLabel(option);

      return typeof option === "object" ? option.name : option;
    },
    [getOptionLabel],
  );

  return (
    <MuiAutocomplete
      disableClearable={disableClearable || !value} // "!value" for back compatibility
      value={value ? value : multiple ? [] : null}
      options={options}
      groupBy={groupBy}
      limitTags={limitTags}
      freeSolo={freeSolo}
      autoSelect={autoSelect}
      clearText={clear}
      disabled={disabled}
      multiple={multiple}
      disableCloseOnSelect={disableCloseOnSelect}
      selectOnFocus={true}
      size={size}
      classes={{
        root: classes.root,
        popper: classes.popper,
      }}
      onChange={(e, option) => {
        onChange(option);
        changeCallback && changeCallback();
      }}
      getOptionDisabled={getOptionDisabled}
      getOptionLabel={handleGetOptionLabel}
      getOptionSelected={(option, value1) => {
        return option.name === value1.name;
      }}
      renderInput={(params) => (
        <TextField
          {...rest}
          {...params}
          {...restInput}
          value={value}
          inputProps={{
            ...params.inputProps,
            // autoComplete: 'new-password', // disable autocomplete and autofill
          }}
        />
      )}
    />
  );
};

export default React.memo(Autocomplete);

const MemorizedInputLabel = React.memo(
  ({ children, ...props }: InputLabelProps) => {
    return <InputLabel {...props}>{children}</InputLabel>;
  },
);

const useTextFieldStyles = makeStyles((theme: Theme) => ({
  muiTextFieldRoot: {
    marginTop: 4,
    marginBottom: -18,
    paddingBottom: 21,
    "& .MuiAutocomplete-popupIndicator": {
      background: "none",
    },

    "& .MuiIconButton-root": {
      color: "#bbbbbb",
    },

    "& .MuiInputBase-input": {
      opacity: "1!important",
      "&:focus": {
        border: "none",
      },
      "&::placeholder": {
        color: "#bcbcbc",
        opacity: "1!important",
      },
      "&:-ms-input-placeholder": {
        color: "#bcbcbc",
      },
      "&::-ms-input-placeholder": {
        color: "#bcbcbc",
      },
    },

    "& .MuiInputBase-input.Mui-disabled": {
      backgroundColor: "unset",
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#c4c4c4",
    },

    "&:hover .Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: "#b3b3b3",
    },

    "&:hover .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline":
      {
        borderColor: theme.palette.error.main,
      },

    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderWidth: 1,
      borderColor: "#7d797a",
    },

    "& .Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.error.main,
    },

    "& .Mui-disabled .MuiOutlinedInput-notchedOutline": {
      backgroundColor: "#bcbcbc33",
    },
  },
  label: {
    fontSize: 14,
    fontWeight: 400,
    color: "#a1a4ac",
    "& > span": {
      lineHeight: "inherit",
    },
  },
  helperText: {
    height: 0,
    fontSize: 14,
    fontWeight: 500,
    textAlign: "right",
    color: "#a1a4ac",
  },
  hasError: {
    color: theme.palette.error.main,
  },
}));

const TextField: FC<FieldRenderProps<any>> = React.memo(
  ({
    label,
    helperText,
    infoIcon,
    meta: { dirtySinceLastSubmit, submitFailed, invalid, error, submitError },
    ...rest
  }) => {
    const localClasses = useTextFieldStyles();
    const hasError = useMemo(
      () => invalid && submitFailed && !dirtySinceLastSubmit,
      [invalid, dirtySinceLastSubmit, submitFailed],
    );
    return (
      <div>
        {!!label && (
          <MemorizedInputLabel className={localClasses.label}>
            {label}{" "}
            {rest.required && (
              <Typography
                className={clsx(
                  localClasses.label,
                  hasError && localClasses.hasError,
                )}
                component={"span"}
                variant={"subtitle2"}
              >
                *
              </Typography>
            )}
            {infoIcon && <InfoIcon text={infoIcon} />}
          </MemorizedInputLabel>
        )}
        <MuiTextField
          // autoComplete='nope'
          variant="outlined"
          error={hasError}
          classes={{
            root: localClasses.muiTextFieldRoot,
          }}
          {...rest}
        />
        {(!!helperText || error || submitError) && (
          <MemorizedInputLabel
            className={clsx(
              localClasses.helperText,
              hasError && localClasses.hasError,
            )}
          >
            {(hasError && (error || submitError)) || helperText}
          </MemorizedInputLabel>
        )}
      </div>
    );
  },
);
